import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { TermsAcceptanceModal } from "./TermsAcceptanceModal";
import { isCircuitOpen, trackQueryResult } from "@/lib/circuit-breaker";

/**
 * Blocks only on auth and terms acceptance. Does not wait for subscription or workspace;
 * those load inside AppLayout so the main UI can paint as soon as auth is ready.
 */
export const SubscriptionGuard = ({ children }: { children: React.ReactNode }) => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [checkingTerms, setCheckingTerms] = useState(true);
  const [needsTermsAcceptance, setNeedsTermsAcceptance] = useState(false);
  const authRetryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!authLoading && !user) {
      // Janela maior + retry para evitar redirecionar prematuramente em PWA mobile,
      // onde getSession() pode demorar a reidratar quando o app volta do background.
      authRetryTimerRef.current = setTimeout(async () => {
        for (let i = 0; i < 3; i++) {
          const { data: { session } } = await supabase.auth.getSession();
          if (session) return;
          if (i < 2) await new Promise((r) => setTimeout(r, 800));
        }
        if (typeof document !== "undefined" && document.visibilityState === "hidden") {
          return;
        }
        navigate("/auth");
      }, 5000);
    }

    return () => {
      if (authRetryTimerRef.current) {
        clearTimeout(authRetryTimerRef.current);
        authRetryTimerRef.current = null;
      }
    };
  }, [user, authLoading, navigate]);

  useEffect(() => {
    const checkTermsAcceptance = async () => {
      if (!user) {
        setCheckingTerms(false);
        return;
      }

      // Circuit breaker: if DB is saturated, skip terms check (permissive mode)
      if (isCircuitOpen()) {
        console.log("[SubscriptionGuard] Circuit breaker OPEN, skipping terms check (permissive)");
        setCheckingTerms(false);
        return;
      }

      try {
        const { data: profile, error } = await supabase
          .from("profiles")
          .select("terms_accepted_at")
          .eq("id", user.id)
          .maybeSingle();

        trackQueryResult(error);

        if (error) {
          console.error("Error checking terms acceptance:", error);
          // Permissive: don't block user on error
          setCheckingTerms(false);
          return;
        }

        setNeedsTermsAcceptance(!profile?.terms_accepted_at);
      } catch (err) {
        console.error("Unexpected error checking terms:", err);
      } finally {
        setCheckingTerms(false);
      }
    };

    if (!authLoading && user) {
      checkTermsAcceptance();
    }
  }, [user, authLoading]);

  const handleTermsAccepted = () => setNeedsTermsAcceptance(false);

  if (authLoading || checkingTerms || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (needsTermsAcceptance) {
    return (
      <>
        <TermsAcceptanceModal isOpen={true} userId={user.id} onAccepted={handleTermsAccepted} />
        <div className="min-h-screen flex items-center justify-center bg-background/80 backdrop-blur-sm">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </>
    );
  }

  return <>{children}</>;
};
